'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { SliceComponentProps } from '@prismicio/react';

/**
 * Props for `HeroCallToAction`.
 */
export type HeroCallToActionProps =
  SliceComponentProps<Content.HeroCallToActionSlice>;

/**
 * Component for "HeroCallToAction" Slices.
 */
const HeroCallToAction: FC<HeroCallToActionProps> = ({ slice }) => {
  const { logo, headline_light, headline_accent, description, cta } =
    slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="hero-section"
    >
      <div className="container">
        {logo && (
          <div className="logo">
            <PrismicNextImage
              field={logo}
              className="w-full h-full object-contain"
              fallbackAlt=""
            />
          </div>
        )}
        <h1 className="headline">
          <span className="headline-light">{headline_light} </span>
          <span className="headline-accent">{headline_accent}</span>
        </h1>

        <p className="subhead">{description}</p>

        <div className="cta">
          <PrismicNextLink field={cta}>{cta.text}</PrismicNextLink>
        </div>
      </div>
      <style jsx>{`
        .hero-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 81px 100px 80px;
          min-height: 674px;
          background: linear-gradient(180deg, #0f423a 0%, #0e1010 100%);
        }
        .container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 60px 100px;
          max-width: 1200px;
          width: 100%;
        }
        .logo {
          height: 120px;
        }
        .headline {
          margin-top: 36px;
          font-style: normal;
          font-size: 42px;
          line-height: 120%;
          text-align: center;
        }

        .headline-light {
          font-weight: 500;
          color: #ffffff;
        }

        .headline-accent {
          font-weight: 600;
          color: #00f557;
        }

        .subhead {
          margin-top: 12px;
          font-style: normal;
          font-weight: 400;
          font-size: 18px;
          line-height: 150%;
          color: #ffffff;
          text-align: center;
        }

        .cta {
          margin-top: 24px;
          padding: 12px 24px;
          gap: 8px;
          height: 48px;
          background: #00f557;
          border-top: 1px solid #00f557;
          border-radius: 100px;
        }

        .cta:hover {
          background-color: rgb(5, 242, 115);
          background-image: linear-gradient(
            to right,
            rgb(0, 242, 165),
            rgb(0, 194, 255)
          );
          --tw-shadow: 0 0 15px #00f2a5cc;
          box-shadow:
            var(--tw-ring-offset-shadow, 0 0 #0000),
            var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
          transition-duration: 0.3s;
          transition-property: all;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }

        @media (max-width: 768px) {
          .hero-section {
            padding: 48px 24px 48px;
            min-height: auto;
          }
          .container {
            padding: 32px 16px;
          }
          .logo {
            height: 80px;
          }
          .headline {
            margin-top: 24px;
            font-size: 28px;
          }
          .subhead {
            font-size: 16px;
          }
          .headline-accent {
            display: block;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .hero-section {
            padding: 60px 48px;
            min-height: auto;
          }
          .container {
            padding: 40px 48px;
          }
          .logo {
            height: 100px;
          }
          .headline {
            margin-top: 28px;
            font-size: 36px;
          }
        }
      `}</style>
    </section>
  );
};

export default HeroCallToAction;
